import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Plus, X } from 'lucide-react';
import api from '../../config/api';
import './FAQ.css';

const defaultFaqs = [
  {
    question: "How do I place an order?",
    answer: "You can browse the menu, add items to your cart, and proceed to checkout. Select your location and preferred payment method to confirm the order."
  },
  {
    question: "How can I track my order?",
    answer: "After placing an order, go to 'My Orders' and click on 'Track'. You can see your rider's live location on the map."
  },
  {
    question: "Can I change or cancel my order after placing it?",
    answer: "You can cancel your order within 60 seconds of placing it. After that, the restaurant starts preparing your food and cancellation may not be possible."
  },
  {
    question: "How do I apply a coupon code?",
    answer: "In the checkout page, you will see a 'Apply Coupon' section. Enter your code there to get discounts."
  },
  {
    question: "What are the payment methods available?",
    answer: "We support Credit/Debit cards, Wallet, and Cash on Delivery in selected regions."
  }
];

const FAQ = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [faqList, setFaqList] = useState(defaultFaqs);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      setLoading(true);
      try {
        const res = await api.get('/cms/faq?limit=100');
        const rawFaqs = res.data?.faqs || res.data;
        if (Array.isArray(rawFaqs) && rawFaqs.length > 0) {
          const formatted = rawFaqs.map(item => ({
            id: item._id,
            question: item.title || item.question || "Frequently Asked Question",
            answer: item.answer || ""
          }));
          setFaqList(formatted);
        }
      } catch (err) {
        console.error("Failed to load FAQs from backend, using default list:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  const filteredFaqs = faqList.filter(f => 
    (f.question && f.question.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (f.answer && f.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="faq-page-container">
      <header className="faq-header">
        <button onClick={() => navigate(-1)} className="faq-back-btn">
          <ArrowLeft size={24} />
        </button>
        <h1>Faq's</h1>
        <div style={{ width: 40 }}></div>
      </header>

      <div className="faq-content">
        <div className="faq-intro">
          <h2>Find quick answers to common questions about orders, payments, delivery, and more.</h2>
        </div>

        <div className="faq-search-wrapper">
          <div className="search-input-box">
            <Search size={20} color="#888" />
            <input 
              type="text" 
              placeholder="Search ..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-3 py-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm flex justify-between items-center">
                <div className="h-5 bg-gray-200 rounded" style={{ width: `${60 + (i % 3) * 15}%` }}></div>
                <div className="w-5 h-5 rounded-full bg-gray-200"></div>
              </div>
            ))}
          </div>
        ) : (

          <div className="faq-list">
            {filteredFaqs.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '20px', color: '#888' }}>No FAQs found.</p>
            ) : (
              filteredFaqs.map((faq, index) => (
                <div 
                  key={faq.id || index} 
                  className={`faq-item ${activeIndex === index ? 'active' : ''}`}
                  onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}
                >
                  <div className="faq-question-row">
                    <span>{faq.question}</span>
                    {activeIndex === index ? (
                      <X size={20} color="#C5192D" />
                    ) : (
                      <Plus size={20} color="#C5192D" />
                    )}
                  </div>
                  {activeIndex === index && (
                    <div className="faq-answer">
                      {typeof faq.answer === 'string' && faq.answer.includes('<') ? (
                        <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                      ) : (
                        faq.answer
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQ;
